/**
 * The HTTP layer.
 *
 * Two decisions worth stating, because both are about correctness rather than
 * taste:
 *
 * 1. Errors are unwrapped into `ApiError` carrying the server's machine `code`.
 *    The UI branches on `code` and never parses prose. plan.md §7 makes this
 *    load-bearing: `plan_limit_reached` is an upgrade prompt and
 *    `permission_denied` is an access error, and they need different copy.
 *
 * 2. The access token lives in a module variable, not localStorage. A token in
 *    localStorage is readable by any script that gets injected, and this app
 *    renders user-supplied text (notes, custom fields). The refresh token does
 *    persist, because a page reload otherwise means signing in again -- which
 *    is the correct trade for a 15-minute access token.
 */
import type {
  Account,
  AccountUsage,
  AccountWrite,
  Activity,
  ActivityWrite,
  Attendance,
  AttendanceCorrection,
  AttendanceCorrectionWrite,
  AttendanceRegularization,
  AttendanceRegularizationWrite,
  AttendanceWrite,
  AuthSession,
  AvailabilityQuery,
  AvailabilitySlot,
  BillingDashboard,
  BillingSchedule,
  BillingSchedulePreview,
  BillingScheduleStatus,
  Booking,
  BookingQuery,
  BookingWrite,
  Branch,
  BranchWrite,
  CompanySummary,
  Contact,
  ContactWrite,
  CreditNote,
  CreditNotePreview,
  CurrentUser,
  CustomerBalanceReport,
  CollectionsReport,
  CreditNotesReport,
  Department,
  DepartmentWrite,
  Employee,
  EmployeeDocument,
  EmployeeDocumentWrite,
  EmployeeFull,
  EmployeePasswordResetResult,
  EmployeePasswordResetWrite,
  EmployeeWrite,
  EmployeeWriteResult,
  DocumentCategory,
  DocumentStatus,
  DocumentVisibility,
  Industry,
  InviteActionResult,
  Invoice,
  InvoiceDocument,
  InvoiceWrite,
  IssuedTokens,
  CatalogSummary,
  Lead,
  LeadBooking,
  LeadCapture,
  Customer,
  LeadQueueSummary,
  LeadQuery,
  LeadWrite,
  LeaveBalance,
  LeaveBalanceAdjustWrite,
  LeaveQuotaAssignmentWrite,
  LeaveRequest,
  LeaveRequestWrite,
  LeaveType,
  LeaveTypeWrite,
  PaginatedActivities,
  PaginatedAttendance,
  PaginatedAttendanceRegularizations,
  PaginatedAuditEvents,
  PaginatedBillingSchedules,
  PaginatedBillingScheduleRuns,
  PaginatedBookings,
  PaginatedCreditNotes,
  PaginatedDepartments,
  PaginatedEmployeeDocuments,
  PaginatedEmployees,
  PaginatedInvoices,
  PaginatedLeads,
  PaginatedLeaveBalances,
  PaginatedLeaveBalanceAudit,
  PaginatedLeaveRequests,
  PaginatedLeaveTypes,
  PaginatedContacts,
  PaginatedOperatorAudit,
  PaginatedPayments,
  PaginatedPayrollItems,
  PaginatedPayrollRuns,
  PaginatedPlatformAccounts,
  PaginatedProducts,
  PaginatedSalaryComponents,
  PaginatedSalaryStructures,
  PaginatedShifts,
  PaginatedShiftTemplates,
  PaginatedTasks,
  PayrollItem,
  PayrollItemAttendance,
  PayrollRun,
  PayrollRunWrite,
  Payment,
  PlatformAccount,
  PlatformAccountWrite,
  PlatformSummary,
  Product,
  RecentActivity,
  ProductOption,
  ProductWrite,
  Receipt,
  ReceivablesAgeing,
  RecurringReport,
  SalesReport,
  Resource,
  ResourceWrite,
  RosterGeneration,
  SalaryComponent,
  SalaryComponentWrite,
  SalaryStructure,
  SalaryStructureWrite,
  Shift,
  ShiftTemplate,
  ShiftTemplateWrite,
  ShiftWrite,
  Task,
  TaskWrite,
  Tax,
  TaxWrite,
  TimelineEntry,
} from "./types";

const BASE = "/api/v1";
const REFRESH_STORAGE_KEY = "sbmone.refresh";

/** Codes the UI must distinguish. Anything else is treated as generic. */
export const ERROR_CODES = {
  planLimitReached: "plan_limit_reached",
  permissionDenied: "permission_denied",
  validationError: "validation_error",
  notAuthenticated: "not_authenticated",
  tokenExpired: "token_not_valid",
} as const;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: Record<string, string[]>;
  readonly detail: unknown;
  readonly requestId: string | undefined;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    fields?: Record<string, string[]> | undefined;
    detail?: unknown;
    requestId?: string | undefined;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields ?? {};
    this.detail = init.detail;
    this.requestId = init.requestId;
  }

  /** True when the user can fix this by paying for a bigger plan. */
  get isPlanLimit(): boolean {
    return this.code === ERROR_CODES.planLimitReached;
  }
}

// --- token storage -------------------------------------------------------

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function readStoredRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    // Private browsing and blocked storage both throw here. Signing in again is
    // an acceptable outcome; crashing is not.
    return null;
  }
}

function writeStoredRefreshToken(token: string): void {
  try {
    window.localStorage.setItem(REFRESH_STORAGE_KEY, token);
  } catch {
    /* see readStoredRefreshToken */
  }
}

/** Adopts a token pair from a sign-in or sign-up response. */
export function storeSession(tokens: IssuedTokens): void {
  accessToken = tokens.access;
  writeStoredRefreshToken(tokens.refresh);
}

export function clearTokens(): void {
  accessToken = null;
  try {
    window.localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    /* see readStoredRefreshToken */
  }
}

/** Registered by AuthProvider so a dead session can drop the UI to sign-in. */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

// --- request pipeline ----------------------------------------------------

interface Envelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
    detail?: unknown;
    request_id?: string;
  };
}

/**
 * Single-flight refresh. Without this, a screen firing four queries on mount
 * gets four 401s, and rotation means the first three refresh tokens are already
 * spent by the time the fourth arrives -- a self-inflicted sign-out.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refresh = readStoredRefreshToken();
  if (!refresh) return null;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!response.ok) return null;

      const tokens = (await response.json()) as IssuedTokens;
      accessToken = tokens.access;
      // Rotation: the server issues a new refresh token every time and the old
      // one is spent, so it must be replaced or the next refresh fails.
      writeStoredRefreshToken(tokens.refresh);
      return tokens.access;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: Envelope = {};
  try {
    body = (await response.json()) as Envelope;
  } catch {
    // A proxy 502 or a Django 500 in debug is HTML, not the envelope. The
    // status still tells the user something went wrong.
  }

  const error = body.error ?? {};
  return new ApiError({
    code: error.code ?? "error",
    message: error.message ?? `Request failed (${response.status}).`,
    status: response.status,
    fields: error.fields ?? {},
    detail: error.detail,
    requestId: error.request_id,
  });
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE" | undefined;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null> | undefined;
  /** Set false for the auth endpoints, which must not carry a stale token. */
  authenticated?: boolean | undefined;
  signal?: AbortSignal | undefined;
}

/** Absolute URL for a path plus query, skipping empty values. */
function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function send<T>(
  path: string,
  options: RequestOptions,
  isRetry: boolean,
): Promise<T> {
  const { method = "GET", body, query, authenticated = true, signal } = options;

  const url = buildUrl(path, query);

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (authenticated && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  if (response.status === 401 && authenticated && !isRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return send<T>(path, options, true);
    clearTokens();
    onUnauthenticated?.();
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Binary sibling of `send` for file downloads. It carries the same auth header
 * and the same single-flight 401 refresh, but returns a Blob instead of JSON --
 * so an export recovers from an expired access token exactly like any other
 * request, rather than surfacing a bare 401 to the user.
 */
async function sendBlob(
  path: string,
  options: RequestOptions,
  isRetry: boolean,
): Promise<Blob> {
  const { query, authenticated = true, signal } = options;

  const url = buildUrl(path, query);

  // No `Accept` header: the API registers only DRF's JSON renderer, so asking
  // for a specific media type (`text/csv`, `application/pdf`) makes content
  // negotiation reject the request with 406 before the download handler runs.
  // The browser's default `*/*` gets through and the handler sets the real
  // `Content-Type` on its response.
  const headers: Record<string, string> = {};
  if (authenticated && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers,
    ...(signal ? { signal } : {}),
  });

  if (response.status === 401 && authenticated && !isRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return sendBlob(path, options, true);
    clearTokens();
    onUnauthenticated?.();
  }

  if (!response.ok) throw await parseError(response);
  return await response.blob();
}

function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return send<T>(path, options, false);
}

// --- endpoints -----------------------------------------------------------

export interface SignInInput {
  email: string;
  password: string;
}

/** What the registration screen posts. The server owns tenant provisioning. */
export interface SignUpInput {
  industry: string;
  business_name: string;
  email: string;
  password: string;
  first_name?: string;
  last_name: string;
}

// A `type` rather than an `interface` on purpose: only type aliases get the
// implicit index signature that makes this assignable to the query record below.
/** `include_inactive` surfaces retired products without hiding them from pickers. */
export type ProductQuery = {
  search?: string | undefined;
  category?: string | undefined;
  include_inactive?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type ContactQuery = {
  search?: string | undefined;
  lifecycle_stage?: string | undefined;
  source?: string | undefined;
  marketing_opt_in?: boolean | undefined;
  /** Comma-separated tag filter (FR-5.7). Each tag narrows further. */
  tags?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type EmployeeListQuery = {
  search?: string | undefined;
  status?: string | undefined;
  department?: string | undefined;
  branch?: string | undefined;
  employment_type?: string | undefined;
  reporting_to?: string | undefined;
  ordering?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type AttendanceQuery = {
  date?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  status?: string | undefined;
  employee?: string | undefined;
  /** Branch id or name. */
  branch?: string | undefined;
  /** Department id or name. */
  department?: string | undefined;
  ordering?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type LeaveRequestQuery = {
  status?: string | undefined;
  leave_type?: string | undefined;
  employee?: string | undefined;
  /** Overlap window used by the roster's approved-leave overlay. */
  from?: string | undefined;
  to?: string | undefined;
  /** `mine` = caller's own requests; `team` = caller's authorized team. */
  scope?: "mine" | "team" | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type PayrollRunQuery = {
  status?: string | undefined;
  year?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type PayrollItemQuery = {
  run?: string | undefined;
  status?: string | undefined;
  employee?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type TaskQuery = {
  status?: string | undefined;
  priority?: string | undefined;
  assignee_employee?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type RosterQuery = {
  from?: string | undefined;
  to?: string | undefined;
  status?: string | undefined;
  employee?: string | undefined;
  template?: string | undefined;
  branch?: string | undefined;
  department?: string | undefined;
  ordering?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type PlatformAccountQuery = {
  status?: string | undefined;
  search?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

/** Default list paging for the People register screens. */
export type PeopleListQuery = {
  employee?: string | undefined;
  leave_type?: string | undefined;
  year?: number | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

export type LeaveBalanceAuditQuery = {
  year?: number | undefined;
  search?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

/** The invoice register's own filters. */
export type InvoiceQuery = {
  status?: string | undefined;
  contact?: string | undefined;
  booking?: string | undefined;
  search?: string | undefined;
  ordering?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

/** The payment register's filters. */
export type PaymentQuery = {
  invoice?: string | undefined;
  contact?: string | undefined;
  direction?: string | undefined;
  method?: string | undefined;
  search?: string | undefined;
  ordering?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

/** The credit-note register's filters. */
export type CreditNoteQuery = {
  status?: string | undefined;
  reason?: string | undefined;
  invoice?: string | undefined;
  contact?: string | undefined;
  search?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

/** The recurring schedule register's filters. */
export type BillingScheduleQuery = {
  status?: BillingScheduleStatus | undefined;
  frequency?: string | undefined;
  contact?: string | undefined;
  search?: string | undefined;
  cursor?: string | undefined;
  page_size?: number | undefined;
};

/** What the "record a payment" form posts. */
export type PaymentInput = {
  invoice: string;
  amount: string;
  method: string;
  reference?: string;
  notes?: string;
  payment_date?: string | null;
  allow_overpayment?: boolean;
};

/** What the "record a refund" form posts. */
export type RefundInput = {
  invoice: string;
  amount: string;
  method: string;
  reference?: string;
  notes?: string;
  payment_date?: string | null;
};

export type CreditNoteItemInput = {
  invoice_item?: string | null;
  description?: string;
  quantity?: string;
  unit_price?: string;
  discount_amount?: string;
  tax_rate?: string;
  tax_type?: string;
  sort_order?: number;
};

/** What the "issue a credit note" form posts. */
export type CreditNoteInput = {
  invoice: string;
  /** Required: a correction must say why it exists. */
  reason: string;
  details?: string;
  issue_date?: string | null;
  items: CreditNoteItemInput[];
};

export type BillingScheduleItemInput = {
  product?: string | null;
  description: string;
  quantity?: string;
  unit_price?: { amount: string; currency?: string } | string;
  discount_amount?: { amount: string; currency?: string } | string;
  tax?: string | null;
  sort_order?: number;
};

/** What the schedule form posts. */
export type BillingScheduleInput = {
  contact: string;
  name: string;
  frequency?: string;
  interval_count?: number;
  start_date: string;
  end_date?: string | null;
  next_invoice_date?: string | null;
  total_occurrences?: number | null;
  auto_generate?: boolean;
  currency?: string;
  place_of_supply?: string;
  inter_state?: boolean;
  payment_terms_days?: number;
  notes?: string;
  terms?: string;
  items: BillingScheduleItemInput[];
};

/** The window and shape of a report request. ``start``/``end`` are ISO dates. */
export type BillingReportQuery = {
  start?: string;
  end?: string;
  months?: number;
};

export type BillingExportKind = "invoices" | "payments" | "receivables" | "credit-notes";

export type InvoiceEmailResult = {
  delivered: boolean;
  backend: string;
  to: string;
  message: string;
};

export const api = {
  signIn: (input: SignInInput) =>
    request<AuthSession>("/auth/signin", {
      method: "POST",
      body: input,
      authenticated: false,
    }),

  /** Create a new company tenant and its Owner, signed in immediately. */
  signUp: (input: SignUpInput) =>
    request<AuthSession>("/auth/signup", {
      method: "POST",
      body: input,
      authenticated: false,
    }),

  /** Confirm an email from a single-use, expiring link token. */
  verifyEmail: (token: string) =>
    request<{ detail: string; email_verified: boolean }>("/auth/verify-email", {
      method: "POST",
      body: { token },
      authenticated: false,
    }),

  /** Re-issue the verification link. Always 204. */
  resendVerification: (email: string) =>
    request<void>("/auth/resend-verification", {
      method: "POST",
      body: { email },
      authenticated: false,
    }),

  /** Accept a staff invite: set the password and sign in. */
  acceptInvite: (input: { token: string; password: string }) =>
    request<AuthSession>("/auth/accept-invite", {
      method: "POST",
      body: input,
      authenticated: false,
    }),

  /** Validate a staff invite token on page load. */
  validateInviteToken: (token: string, signal?: AbortSignal) =>
    request<{ valid: boolean; email: string; first_name: string; last_name: string }>(
      "/auth/accept-invite",
      {
        query: { token },
        signal,
        authenticated: false,
      },
    ),

  /** Set a new password from a single-use, expiring password-reset token. */
  passwordResetConfirm: (token: string, password: string) =>
    request<{ detail: string }>("/auth/password-reset/confirm", {
      method: "POST",
      body: { token, password },
      authenticated: false,
    }),

  me: (signal?: AbortSignal) => request<CurrentUser>("/me", { signal }),

  industries: (signal?: AbortSignal) =>
    request<Industry[]>("/industries", { signal, authenticated: false }),

  contacts: (params: ContactQuery, signal?: AbortSignal) =>
    request<PaginatedContacts>("/contacts", { query: params, signal }),

  contact: (id: string, signal?: AbortSignal) =>
    request<Contact>(`/contacts/${encodeURIComponent(id)}`, { signal }),

  /** Usage against plan limits. Drives every usage bar in the product. */
  usage: (accountId: string, signal?: AbortSignal) =>
    request<AccountUsage>(`/account/${encodeURIComponent(accountId)}/usage`, { signal }),

  /** The signed-in account's own settings. Owner/admin may write. */
  account: (accountId: string, signal?: AbortSignal) =>
    request<Account>(`/account/${encodeURIComponent(accountId)}`, { signal }),
  updateAccount: (accountId: string, body: AccountWrite) =>
    request<Account>(`/account/${encodeURIComponent(accountId)}`, {
      method: "PATCH",
      body,
    }),

  /** Mark the owner's first-time setup complete so it stops reappearing. */
  completeOnboarding: (accountId: string) =>
    request<Account>(`/account/${encodeURIComponent(accountId)}/complete-onboarding`, {
      method: "POST",
    }),

  createContact: (body: ContactWrite) =>
    request<Contact>("/contacts", { method: "POST", body }),

  updateContact: (id: string, body: Partial<ContactWrite>) =>
    request<Contact>(`/contacts/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  deleteContact: (id: string) =>
    request<void>(`/contacts/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** FR-5.1: reversible within 30 days, and safe to retry. */
  restoreContact: (id: string) =>
    request<Contact>(`/contacts/${encodeURIComponent(id)}/restore`, { method: "POST" }),

  /** The 30-day Bin. Without it a recoverable delete is unusable. */
  trash: (signal?: AbortSignal) =>
    request<PaginatedContacts>("/contacts/trash", { signal }),

  activities: (contactId: string, signal?: AbortSignal) =>
    request<PaginatedActivities>(`/contacts/${encodeURIComponent(contactId)}/activities`, {
      signal,
    }),

  // --- Catalogue ---

  products: (params: ProductQuery = {}, signal?: AbortSignal) =>
    request<PaginatedProducts>("/products", { query: params, signal }),

  product: (id: string, signal?: AbortSignal) =>
    request<Product>(`/products/${encodeURIComponent(id)}`, { signal }),

  createProduct: (body: ProductWrite) =>
    request<Product>("/products", { method: "POST", body }),

  updateProduct: (id: string, body: Partial<ProductWrite>) =>
    request<Product>(`/products/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  deleteProduct: (id: string) =>
    request<void>(`/products/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** Compact list for pickers. No margin: this shape faces customers. */
  productOptions: (signal?: AbortSignal) =>
    request<ProductOption[]>("/products/options", { signal }),

  catalogSummary: (signal?: AbortSignal) =>
    request<CatalogSummary>("/products/summary", { signal }),

  logActivity: (contactId: string, body: ActivityWrite) =>
    request<Activity>(`/contacts/${encodeURIComponent(contactId)}/activities`, {
      method: "POST",
      body,
    }),

  // --- Enquiries ---

  leads: (_query?: LeadQuery, signal?: AbortSignal, opts?: { contactId: string }) =>
    opts?.contactId
      ? request<PaginatedLeads>(`/contacts/${encodeURIComponent(opts.contactId)}/leads`, { signal })
      : request<PaginatedLeads>("/leads", { query: _query as ProductQuery, signal }),

  /** The product's name, price and duration are snapshotted server-side. */
  addLead: (contactId: string, body: LeadWrite) =>
    request<Lead>(`/contacts/${encodeURIComponent(contactId)}/leads`, {
      method: "POST",
      body,
    }),

  // --- The lead queue ---------------------------------------------------

  /**
   * The lead queue, or one contact's leads.
   *
   * Defaults to open leads, oldest first -- that default *is* the work queue.
   * Passing `contactId` reads the nested endpoint instead, so the list screen
   * and the queue share one call site and one query key shape.
   */

  lead: (id: string, signal?: AbortSignal) =>
    request<Lead>(`/leads/${encodeURIComponent(id)}`, { signal }),

  captureLead: (body: LeadCapture) => request<Lead>("/leads", { method: "POST", body }),

  updateLead: (id: string, body: Partial<LeadCapture>) =>
    request<Lead>(`/leads/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  /** Only the transitions the lifecycle allows; others come back 400. */
  setLeadStatus: (id: string, status: string) =>
    request<Lead>(`/leads/${encodeURIComponent(id)}/status`, { method: "POST", body: { status } }),

  /** FR-6.4. Idempotent: a second call returns the same contact. */
  convertLead: (id: string) =>
    request<Contact>(`/leads/${encodeURIComponent(id)}/convert`, { method: "POST" }),

  /** Log an activity against a lead. Attached by `related_type`, not to a contact. */
  logLeadActivity: (id: string, body: ActivityWrite) =>
    request<Activity>(`/leads/${encodeURIComponent(id)}/activities`, {
      method: "POST",
      body,
    }),

  /**
   * Recent activity for a whole page of leads in one request.
   *
   * Per-lead fetches would be one request per row, so the queue would issue a
   * hundred calls to draw a list. Leads with nothing logged are omitted.
   */
  leadRecentActivities: (leadIds: string[], limit = 3, signal?: AbortSignal) =>
    request<RecentActivity[]>("/leads/recent-activities", {
      method: "POST",
      body: { lead_ids: leadIds, limit },
      signal,
    }),

  /** They agreed the time. Idempotent -- confirming twice keeps the first stamp. */
  confirmLead: (id: string) =>
    request<Lead>(`/leads/${encodeURIComponent(id)}/confirm`, { method: "POST" }),

  /** One lead's whole history, for the detail modal. */
  leadActivities: (id: string, signal?: AbortSignal) =>
    request<Activity[]>(`/leads/${encodeURIComponent(id)}/activities`, { signal }),

  /** Partial per-lead outcome: some ids may be refused. */
  bulkLeadStatus: (leadIds: string[], status: string) =>
    request<{ updated: string[]; refused: Array<{ id: string; reason: string }> }>(
      "/leads/bulk-status",
      { method: "POST", body: { lead_ids: leadIds, status } },
    ),

  /**
   * The people who became customers -- converted leads, deduplicated by the
   * contact each became. A lens, not a fourth copy of a person.
   */
  customers: (signal?: AbortSignal) => request<Customer[]>("/leads/customers", { signal }),

  /** The employment records, for an owner dropdown. */
  employees: (signal?: AbortSignal) =>
    request<Employee[]>("/employees/options", { signal }),

  /** The same directory, narrowed server-side for the balance search box. */
  employeeOptions: (params: EmployeeListQuery = {}, signal?: AbortSignal) =>
    request<Employee[]>("/employees/options", { query: params, signal }),

  leadSummary: (signal?: AbortSignal) =>
    request<LeadQueueSummary>("/leads/summary", { signal }),

  deleteLead: (id: string, options: { confirm?: boolean } = {}) =>
    request<void>(`/leads/${encodeURIComponent(id)}`, {
      method: "DELETE",
      query: options.confirm ? { confirm: "true" } : {},
    }),

  // --- Scheduling ----------------------------------------------------

  /**
   * The bookings register, or one provider's window.
   *
   * The calendar passes its `from`/`to` so the whole week comes back in one
   * request; the list passes its filters. Cancelled bookings are excluded
   * unless `include_cancelled` is asked, because the register does not want to
   * read failures out loud but the day view refuses to forget them.
   */
  bookings: (params: BookingQuery, signal?: AbortSignal) =>
    request<PaginatedBookings>("/bookings", { query: params, signal }),

  booking: (id: string, signal?: AbortSignal) =>
    request<Booking>(`/bookings/${encodeURIComponent(id)}`, { signal }),

  createBooking: (body: BookingWrite) =>
    request<Booking>("/bookings", { method: "POST", body }),

  /** FR-10.6: rescheduling edits the same row, history intact. */
  updateBooking: (id: string, body: Partial<BookingWrite>) =>
    request<Booking>(`/bookings/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  /** FR-10.2/10.3: the server has already decided every slot is free. */
  bookingAvailability: (params: AvailabilityQuery, signal?: AbortSignal) =>
    request<AvailabilitySlot[]>("/bookings/availability", { query: params, signal }),

  /** FR-10.5: a cancellation without a reason is a hole in the metrics. */
  cancelBooking: (id: string, reason: string) =>
    request<Booking>(`/bookings/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: { reason },
    }),

  /** FR-10.4: only the transitions the lifecycle allows. */
  setBookingStatus: (id: string, status: string) =>
    request<Booking>(`/bookings/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: { status },
    }),

  resources: (signal?: AbortSignal) =>
    request<Resource[]>("/resources", { signal }),

  createResource: (body: ResourceWrite) =>
    request<Resource>("/resources", { method: "POST", body }),

  updateResource: (id: string, body: Partial<ResourceWrite>) =>
    request<Resource>(`/resources/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  /**
   * FR-6.5: the agreed slot's fate, for the lead panel.
   *
   * After winning or confirming a lead the UI asks whether the slot got held
   * and, if not, reads the `blocked_reason` to offer the exact next step.
   */
  leadBooking: (leadId: string, signal?: AbortSignal) =>
    request<LeadBooking>(`/leads/${encodeURIComponent(leadId)}/booking`, { signal }),

  /** Holds the agreed slot now. Idempotent; a 409 means it is taken. */
  bookLead: (leadId: string) =>
    request<Booking>(`/leads/${encodeURIComponent(leadId)}/book`, { method: "POST" }),

  /** FR-5.4: one feed, every source. Newest first. */
  timeline: (contactId: string, signal?: AbortSignal) =>
    request<TimelineEntry[]>(`/contacts/${encodeURIComponent(contactId)}/timeline`, { signal }),

  /**
   * FR-14.2: CSV of the current view.
   *
   * Fetched as a blob through the same authenticated path rather than by
   * handing the browser a URL: the export has to carry the bearer token, and a
   * plain link or `window.open` cannot.
   *
   * `Accept` is deliberately left unset. DRF performs content negotiation on the
   * request before the handler runs, and the API configures only
   * `JSONRenderer` -- so asking for `text/csv` returns 406 Not Acceptable and
   * the export silently fails. The browser's default `any media type` gets
   * through, and the response really is CSV.
   */
  exportContactsCsv: async (query: ContactQuery = {}): Promise<Blob> => {
    const url = buildUrl("/contacts/export", query);
    const response = await fetch(url, {
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
    if (!response.ok) throw await parseError(response);
    return response.blob();
  },

  // --- People ---------------------------------------------------------

  /** Full employment records, paginated. The dropdown lives in `employees`. */
  employeeList: (params: EmployeeListQuery = {}, signal?: AbortSignal) =>
    request<PaginatedEmployees>("/employees", { query: params, signal }),

  employee: (id: string, signal?: AbortSignal) =>
    request<EmployeeFull>(`/employees/${encodeURIComponent(id)}`, { signal }),

  /** The signed-in user's own employment record (404 when none is linked). */
  employeeMe: (signal?: AbortSignal) =>
    request<EmployeeFull>("/employees/me", { signal }),

  /** Self-service update of the signed-in user's own permitted fields. */
  updateEmployeeMe: (body: Partial<EmployeeWrite>) =>
    request<EmployeeWriteResult>("/employees/me", { method: "PATCH", body }),

  createEmployee: (body: EmployeeWrite) =>
    request<EmployeeWriteResult>("/employees", { method: "POST", body }),

  updateEmployee: (id: string, body: Partial<EmployeeWrite>) =>
    request<EmployeeWriteResult>(`/employees/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  /** Soft delete: the row survives for payroll and attendance history. */
  deactivateEmployee: (id: string) =>
    request<void>(`/employees/${encodeURIComponent(id)}/deactivate`, { method: "POST" }),

  reactivateEmployee: (id: string) =>
    request<EmployeeFull>(`/employees/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),

  /**
   * Assign an employee's login role. Owner/admin only; nobody may change their
   * own role, and only a true owner may grant the owner role.
   */
  setEmployeeRole: (id: string, role: "owner" | "manager" | "staff") =>
    request<EmployeeFull>(`/employees/${encodeURIComponent(id)}/set-role`, {
      method: "POST",
      body: { role },
    }),

  /**
   * Set a new password for an employee's existing login. Owner/admin only.
   * Role and seat usage are unchanged; an outstanding invitation is consumed.
   */
  resetEmployeePassword: (id: string, body: EmployeePasswordResetWrite) =>
    request<EmployeePasswordResetResult>(
      `/employees/${encodeURIComponent(id)}/reset-password`,
      { method: "POST", body },
    ),

  /** Re-issue a pending invitation. The previous link stops working at once. */
  resendEmployeeInvite: (id: string) =>
    request<InviteActionResult>(`/employees/${encodeURIComponent(id)}/resend-invite`, {
      method: "POST",
    }),

  /** Cancel a pending invitation so its link can no longer be used. */
  revokeEmployeeInvite: (id: string) =>
    request<InviteActionResult>(`/employees/${encodeURIComponent(id)}/revoke-invite`, {
      method: "POST",
    }),

  /** Bulk generate invitation URLs for all employees lacking an active invitation. */
  generateEmployeeInvites: () =>
    request<{
      generated_count: number;
      skipped_count: number;
      invitations: Array<{
        id: string;
        employee_code: string;
        display_name: string;
        email: string;
        invite_status: string;
        invitation_url: string;
        invite_token: string;
        invite_expires_at: string;
        already_had_invite: boolean;
      }>;
    }>("/employees/generate-invites", {
      method: "POST",
    }),

  /** Upload a passport-size photo; stored as a data URL so it renders inline. */
  uploadEmployeePhoto: async (id: string, file: File): Promise<EmployeeFull> => {
    const form = new FormData();
    form.append("photo", file);
    const response = await fetch(buildUrl(`/employees/${encodeURIComponent(id)}/photo`), {
      method: "POST",
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: form,
    });
    if (!response.ok) throw await parseError(response);
    return (await response.json()) as EmployeeFull;
  },

  /** Clear an employee's stored photo. */
  removeEmployeePhoto: async (id: string): Promise<EmployeeFull> => {
    const form = new FormData();
    form.append("remove", "true");
    const response = await fetch(buildUrl(`/employees/${encodeURIComponent(id)}/photo`), {
      method: "POST",
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: form,
    });
    if (!response.ok) throw await parseError(response);
    return (await response.json()) as EmployeeFull;
  },

  departmentsPage: (params: PeopleListQuery = {}, signal?: AbortSignal) =>
    request<PaginatedDepartments>("/departments", { query: params, signal }),

  createDepartment: (body: DepartmentWrite) =>
    request<Department>("/departments", { method: "POST", body }),

  updateDepartment: (id: string, body: Partial<DepartmentWrite>) =>
    request<Department>(`/departments/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  deleteDepartment: (id: string) =>
    request<void>(`/departments/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // --- Organization: branches -----------------------------------------

  branches: (signal?: AbortSignal) => request<Branch[]>("/branches", { signal }),

  createBranch: (body: BranchWrite) =>
    request<Branch>("/branches", { method: "POST", body }),

  updateBranch: (id: string, body: Partial<BranchWrite>) =>
    request<Branch>(`/branches/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  deleteBranch: (id: string) =>
    request<void>(`/branches/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // --- Employee documents ---------------------------------------------

  employeeDocuments: (
    params: { employee?: string; category?: string; status?: string; cursor?: string; page_size?: number } = {},
    signal?: AbortSignal,
  ) => request<PaginatedEmployeeDocuments>("/employee-documents", { query: params, signal }),

  /** Multipart upload; the request helper serialises JSON, so this is raw fetch. */
  uploadEmployeeDocument: async (body: EmployeeDocumentWrite): Promise<EmployeeDocument> => {
    const form = new FormData();
    form.append("employee", body.employee);
    form.append("name", body.name);
    form.append("file", body.file);
    if (body.category) form.append("category", body.category);
    if (body.status) form.append("status", body.status);
    if (body.visibility) form.append("visibility", body.visibility);
    if (body.expiry_date) form.append("expiry_date", body.expiry_date);
    if (body.notes) form.append("notes", body.notes);
    const response = await fetch(buildUrl("/employee-documents"), {
      method: "POST",
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: form,
    });
    if (!response.ok) throw await parseError(response);
    return (await response.json()) as EmployeeDocument;
  },

  updateEmployeeDocument: (
    id: string,
    body: Partial<{
      name: string;
      category: DocumentCategory;
      status: DocumentStatus;
      visibility: DocumentVisibility;
      expiry_date: string | null;
      notes: string;
    }>,
  ) =>
    request<EmployeeDocument>(`/employee-documents/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),

  deleteEmployeeDocument: (id: string) =>
    request<void>(`/employee-documents/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** Permission-checked download; not a public media URL. */
  downloadEmployeeDocument: async (id: string): Promise<Blob> => {
    const response = await fetch(
      buildUrl(`/employee-documents/${encodeURIComponent(id)}/download`),
      { headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) } },
    );
    if (!response.ok) throw await parseError(response);
    return response.blob();
  },

  attendancePage: (params: AttendanceQuery = {}, signal?: AbortSignal) =>
    request<PaginatedAttendance>("/attendance", { query: params, signal }),

  createAttendance: (body: AttendanceWrite) =>
    request<Attendance>("/attendance", { method: "POST", body }),

  /**
   * Self-service or admin check-in. Admin may pass an `employee`.
   *
   * `latitude`/`longitude`/`accuracy` are the device's optional GPS reading;
   * the request IP is derived server-side and is never sent by the client.
   */
  punchIn: (body: {
    employee?: string;
    work_date?: string;
    note?: string;
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  }) => request<Attendance>("/attendance/punch-in", { method: "POST", body }),

  punchOut: (body: { employee?: string; work_date?: string }) =>
    request<Attendance>("/attendance/punch-out", { method: "POST", body }),

  submitAttendanceReport: (body: {
    employee?: string;
    work_date?: string;
    work_done: string;
    learning: string;
  }) =>
    request<Attendance>("/attendance/submit-report", { method: "POST", body }),

  /** Admin correction with an audit trail; never a silent overwrite. */
  correctAttendance: (id: string, body: AttendanceCorrectionWrite) =>
    request<Attendance>(`/attendance/${encodeURIComponent(id)}/correct`, { method: "POST", body }),

  attendanceCorrections: (id: string, signal?: AbortSignal) =>
    request<AttendanceCorrection[]>(`/attendance/${encodeURIComponent(id)}/corrections`, { signal }),

  // --- Attendance regularization --------------------------------------
  // An employee asks for a correction; their reporting manager decides it.

  attendanceRegularizations: (
    params: { status?: string; employee?: string; cursor?: string; page_size?: number } = {},
    signal?: AbortSignal,
  ) =>
    request<PaginatedAttendanceRegularizations>("/attendance-regularizations", {
      query: params,
      signal,
    }),

  createAttendanceRegularization: (body: AttendanceRegularizationWrite) =>
    request<AttendanceRegularization>("/attendance-regularizations", { method: "POST", body }),

  approveAttendanceRegularization: (id: string, note?: string) =>
    request<AttendanceRegularization>(
      `/attendance-regularizations/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: { note: note ?? "" } },
    ),

  rejectAttendanceRegularization: (id: string, note?: string) =>
    request<AttendanceRegularization>(
      `/attendance-regularizations/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: { note: note ?? "" } },
    ),

  cancelAttendanceRegularization: (id: string) =>
    request<AttendanceRegularization>(
      `/attendance-regularizations/${encodeURIComponent(id)}/cancel`,
      { method: "POST" },
    ),

  leaveTypesPage: (params: PeopleListQuery = {}, signal?: AbortSignal) =>
    request<PaginatedLeaveTypes>("/leave-types", { query: params, signal }),

  createLeaveType: (body: LeaveTypeWrite) =>
    request<LeaveType>("/leave-types", { method: "POST", body }),

  updateLeaveType: (id: string, body: Partial<LeaveTypeWrite>) =>
    request<LeaveType>(`/leave-types/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  leaveBalances: (params: PeopleListQuery = {}, signal?: AbortSignal) =>
    request<PaginatedLeaveBalances>("/leave-balances", { query: params, signal }),

  /** Paginated audit rows; employees without a quota are included. */
  leaveBalanceAudit: (params: LeaveBalanceAuditQuery = {}, signal?: AbortSignal) =>
    request<PaginatedLeaveBalanceAudit>("/leave-balances/audit", {
      query: params,
      signal,
    }),

  /** Initialise an employee-year's quotas. Creation only; 400 if any exists. */
  assignLeaveQuota: (body: LeaveQuotaAssignmentWrite) =>
    request<LeaveBalance[]>("/leave-balances/assign-quota", {
      method: "POST",
      body,
    }),

  /** Adjust an existing allocation; used/pending days stay untouched. */
  updateLeaveBalance: (id: string, body: LeaveBalanceAdjustWrite) =>
    request<LeaveBalance>(`/leave-balances/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),

  leaveRequests: (params: LeaveRequestQuery = {}, signal?: AbortSignal) =>
    request<PaginatedLeaveRequests>("/leave-requests", { query: params, signal }),

  createLeaveRequest: (body: LeaveRequestWrite) => {
    const payload = { ...body };
    // A null reason is not "no reason": omit it so the backend reports the
    // required field instead of rejecting a silently corrupted null value.
    if (payload.reason == null) delete (payload as { reason?: string }).reason;
    // An empty employee means "file for myself": omit it so the backend resolves
    // the requester's own employee record instead of rejecting a null employee.
    if (!payload.employee) delete (payload as { employee?: string }).employee;
    return request<LeaveRequest>("/leave-requests", { method: "POST", body: payload });
  },

  approveLeaveRequest: (id: string, note?: string) =>
    request<LeaveRequest>(`/leave-requests/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: { note: note ?? "" },
    }),

  rejectLeaveRequest: (id: string, note?: string) =>
    request<LeaveRequest>(`/leave-requests/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      body: { note: note ?? "" },
    }),

  cancelLeaveRequest: (id: string) =>
    request<LeaveRequest>(`/leave-requests/${encodeURIComponent(id)}/cancel`, { method: "POST" }),

  salaryComponents: (signal?: AbortSignal) =>
    request<PaginatedSalaryComponents>("/salary-components", { signal }),

  createSalaryComponent: (body: SalaryComponentWrite) =>
    request<SalaryComponent>("/salary-components", { method: "POST", body }),

  updateSalaryComponent: (id: string, body: Partial<SalaryComponentWrite>) =>
    request<SalaryComponent>(`/salary-components/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  deleteSalaryComponent: (id: string) =>
    request<void>(`/salary-components/${encodeURIComponent(id)}`, { method: "DELETE" }),

  salaryStructures: (params: PeopleListQuery = {}, signal?: AbortSignal) =>
    request<PaginatedSalaryStructures>("/salary-structures", { query: params, signal }),

  /** The signed-in employee's own current structure. `null` when there is none. */
  mySalaryStructure: (signal?: AbortSignal) =>
    request<SalaryStructure | null>("/salary-structures/me", { signal }),

  createSalaryStructure: (body: SalaryStructureWrite) =>
    request<SalaryStructure>("/salary-structures", { method: "POST", body }),

  updateSalaryStructure: (id: string, body: Partial<SalaryStructureWrite>) =>
    request<SalaryStructure>(`/salary-structures/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  payrollRuns: (params: PayrollRunQuery = {}, signal?: AbortSignal) =>
    request<PaginatedPayrollRuns>("/payroll-runs", { query: params, signal }),

  createPayrollRun: (body: PayrollRunWrite) =>
    request<PayrollRun>("/payroll-runs", { method: "POST", body }),

  payrollRunItems: (runId: string, signal?: AbortSignal) =>
    request<PayrollItem[]>(`/payroll-runs/${encodeURIComponent(runId)}/items`, { signal }),

  /** The attendance breakdown behind one payslip. */
  payrollItemAttendance: (itemId: string, signal?: AbortSignal) =>
    request<PayrollItemAttendance>(`/payslips/${encodeURIComponent(itemId)}/attendance`, { signal }),

  computePayrollRun: (id: string) =>
    request<PayrollRun>(`/payroll-runs/${encodeURIComponent(id)}/compute`, { method: "POST" }),

  approvePayrollRun: (id: string) =>
    request<PayrollRun>(`/payroll-runs/${encodeURIComponent(id)}/approve`, { method: "POST" }),

  payPayrollRun: (id: string) =>
    request<PayrollRun>(`/payroll-runs/${encodeURIComponent(id)}/pay`, { method: "POST" }),

  lockPayrollRun: (id: string) =>
    request<PayrollRun>(`/payroll-runs/${encodeURIComponent(id)}/lock`, { method: "POST" }),

  cancelPayrollRun: (id: string) =>
    request<PayrollRun>(`/payroll-runs/${encodeURIComponent(id)}/cancel`, { method: "POST" }),

  payslips: (params: PayrollItemQuery = {}, signal?: AbortSignal) =>
    request<PaginatedPayrollItems>("/payslips", { query: params, signal }),

  /** Download the printable, self-contained payslip for one item. */
  payslipDownload: (itemId: string, signal?: AbortSignal): Promise<Blob> =>
    sendBlob(`/payslips/${encodeURIComponent(itemId)}/download`, { signal }, false),

  tasksPage: (params: TaskQuery = {}, signal?: AbortSignal) =>
    request<PaginatedTasks>("/tasks", { query: params, signal }),

  createTask: (body: TaskWrite) => request<Task>("/tasks", { method: "POST", body }),

  updateTask: (id: string, body: Partial<TaskWrite>) =>
    request<Task>(`/tasks/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  tasksMine: (signal?: AbortSignal) => request<Task[]>("/tasks/mine", { signal }),

  startTask: (id: string) =>
    request<Task>(`/tasks/${encodeURIComponent(id)}/start`, { method: "POST" }),

  completeTask: (id: string) =>
    request<Task>(`/tasks/${encodeURIComponent(id)}/complete`, { method: "POST" }),

  reopenTask: (id: string) =>
    request<Task>(`/tasks/${encodeURIComponent(id)}/reopen`, { method: "POST" }),

  cancelTask: (id: string) =>
    request<Task>(`/tasks/${encodeURIComponent(id)}/cancel`, { method: "POST" }),

  // --- Roster ---------------------------------------------------------

  shiftTemplatesPage: (params: PeopleListQuery = {}, signal?: AbortSignal) =>
    request<PaginatedShiftTemplates>("/shift-templates", { query: params, signal }),

  createShiftTemplate: (body: ShiftTemplateWrite) =>
    request<ShiftTemplate>("/shift-templates", { method: "POST", body }),

  updateShiftTemplate: (id: string, body: Partial<ShiftTemplateWrite>) =>
    request<ShiftTemplate>(`/shift-templates/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),

  deleteShiftTemplate: (id: string) =>
    request<void>(`/shift-templates/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** FR-17.5: bulk-roster a template, with leave and conflicts as exceptions. */
  generateRoster: (
    templateId: string,
    body: { employees: string[]; from_date: string; to_date: string },
  ) =>
    request<RosterGeneration>(
      `/shift-templates/${encodeURIComponent(templateId)}/generate`,
      { method: "POST", body },
    ),

  shiftsPage: (params: RosterQuery = {}, signal?: AbortSignal) =>
    request<PaginatedShifts>("/shifts", { query: params, signal }),

  createShift: (body: ShiftWrite) =>
    request<Shift>("/shifts", { method: "POST", body }),

  updateShift: (id: string, body: Partial<ShiftWrite>) =>
    request<Shift>(`/shifts/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  deleteShift: (id: string) =>
    request<void>(`/shifts/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** FR-17.2/17.4: publishing is what makes the employee bookable. */
  publishShift: (id: string) =>
    request<Shift>(`/shifts/${encodeURIComponent(id)}/publish`, { method: "POST" }),

  unpublishShift: (id: string) =>
    request<Shift>(`/shifts/${encodeURIComponent(id)}/unpublish`, { method: "POST" }),

  // --- Billing ---------------------------------------------------------

  /**
   * The invoice register. Reading is any member; the list is all this screen
   * needs. Drafting, payments and credit notes are separate work.
   */
  invoices: (params: InvoiceQuery = {}, signal?: AbortSignal) =>
    request<PaginatedInvoices>("/invoices", { query: params, signal }),

  /** Start a draft. Totals are computed server-side from the lines. */
  createInvoice: (body: InvoiceWrite) =>
    request<Invoice>("/invoices", { method: "POST", body }),

  invoice: (id: string, signal?: AbortSignal) =>
    request<Invoice>(`/invoices/${encodeURIComponent(id)}`, { signal }),

  /** The printable invoice document: identity, dates, lines, totals, payments. */
  invoiceDocument: (id: string, signal?: AbortSignal) =>
    request<InvoiceDocument>(`/invoices/${encodeURIComponent(id)}/document`, { signal }),

  /** Download the self-contained printable HTML invoice. */
  invoiceDownload: (id: string, signal?: AbortSignal): Promise<Blob> =>
    sendBlob(`/invoices/${encodeURIComponent(id)}/download`, { signal }, false),

  /** Email the invoice to the customer's address on file. Manager and above. */
  emailInvoice: (id: string) =>
    request<InvoiceEmailResult>(`/invoices/${encodeURIComponent(id)}/email`, {
      method: "POST",
    }),

  /** Send a manual payment reminder for an outstanding invoice. */
  remindInvoice: (id: string) =>
    request<InvoiceEmailResult>(`/invoices/${encodeURIComponent(id)}/remind`, {
      method: "POST",
    }),

  /** Edit a draft. An issued invoice is immutable -- the server refuses it. */
  updateInvoice: (id: string, body: Partial<InvoiceWrite>) =>
    request<Invoice>(`/invoices/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  /** Assign a number and move a draft to sent. Manager and above (FR-23.3). */
  issueInvoice: (id: string) =>
    request<Invoice>(`/invoices/${encodeURIComponent(id)}/issue`, { method: "POST" }),

  /** Void an unpaid invoice with a reason. Owner/admin only (FR-23.5). */
  voidInvoice: (id: string, reason: string) =>
    request<Invoice>(`/invoices/${encodeURIComponent(id)}/void`, {
      method: "POST",
      body: { reason },
    }),

  // --- Payments and receipts (FR-25) ----------------------------------

  /** The payment register, or one invoice's receipts. */
  payments: (params: PaymentQuery = {}, signal?: AbortSignal) =>
    request<PaginatedPayments>("/payments", { query: params, signal }),

  payment: (id: string, signal?: AbortSignal) =>
    request<Payment>(`/payments/${encodeURIComponent(id)}`, { signal }),

  /** Record a manual receipt. Manager and above. */
  recordPayment: (body: PaymentInput) =>
    request<Payment>("/payments", { method: "POST", body }),

  /** Refund collected money. Owner/admin only. */
  refundPayment: (body: RefundInput) =>
    request<Payment>("/payments/refund", { method: "POST", body }),

  /** The printable receipt for one payment. */
  paymentReceipt: (id: string, signal?: AbortSignal) =>
    request<Receipt>(`/payments/${encodeURIComponent(id)}/receipt`, { signal }),

  // --- Credit notes (FR-26) -------------------------------------------

  creditNotes: (params: CreditNoteQuery = {}, signal?: AbortSignal) =>
    request<PaginatedCreditNotes>("/credit-notes", { query: params, signal }),

  creditNote: (id: string, signal?: AbortSignal) =>
    request<CreditNote>(`/credit-notes/${encodeURIComponent(id)}`, { signal }),

  /** Issue a correction against an issued invoice. Manager and above. */
  createCreditNote: (body: CreditNoteInput) =>
    request<CreditNote>("/credit-notes", { method: "POST", body }),

  /**
   * Compute a credit note's totals without issuing it. The figures shown while
   * the form is filled come from the server, so tax is never re-derived here.
   */
  previewCreditNote: (body: CreditNoteInput) =>
    request<CreditNotePreview>("/credit-notes/preview", { method: "POST", body }),

  /** Void a credit note and put the money back. Owner/admin only. */
  voidCreditNote: (id: string, reason: string) =>
    request<CreditNote>(`/credit-notes/${encodeURIComponent(id)}/void`, {
      method: "POST",
      body: { reason },
    }),

  /** Refund a credit note. Owner/admin only. */
  refundCreditNote: (
    id: string,
    body: {
      amount: string;
      method: string;
      reference?: string;
      notes?: string;
      payment_date?: string | null;
    },
  ) =>
    request<Payment>(`/credit-notes/${encodeURIComponent(id)}/refund`, {
      method: "POST",
      body,
    }),

  creditNoteHistory: (id: string, signal?: AbortSignal) =>
    request<PaginatedAuditEvents>(`/credit-notes/${encodeURIComponent(id)}/history`, {
      signal,
    }),

  // --- Recurring schedules (FR-27) ------------------------------------

  billingSchedules: (params: BillingScheduleQuery = {}, signal?: AbortSignal) =>
    request<PaginatedBillingSchedules>("/billing-schedules", { query: params, signal }),

  billingSchedule: (id: string, signal?: AbortSignal) =>
    request<BillingSchedule>(`/billing-schedules/${encodeURIComponent(id)}`, { signal }),

  createBillingSchedule: (body: BillingScheduleInput) =>
    request<BillingSchedule>("/billing-schedules", { method: "POST", body }),

  updateBillingSchedule: (id: string, body: Partial<BillingScheduleInput>) =>
    request<BillingSchedule>(`/billing-schedules/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),

  deleteBillingSchedule: (id: string) =>
    request<void>(`/billing-schedules/${encodeURIComponent(id)}`, { method: "DELETE" }),

  pauseBillingSchedule: (id: string) =>
    request<BillingSchedule>(`/billing-schedules/${encodeURIComponent(id)}/pause`, {
      method: "POST",
    }),

  resumeBillingSchedule: (id: string) =>
    request<BillingSchedule>(`/billing-schedules/${encodeURIComponent(id)}/resume`, {
      method: "POST",
    }),

  cancelBillingSchedule: (id: string) =>
    request<BillingSchedule>(`/billing-schedules/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
    }),

  /** Generate this schedule's next invoice now. */
  generateBillingSchedule: (id: string) =>
    request<BillingSchedule>(`/billing-schedules/${encodeURIComponent(id)}/generate`, {
      method: "POST",
    }),

  /**
   * Preview the next invoice. Pass a saved schedule's id, or a full body while
   * the form is still being filled in. Totals come from the server.
   */
  previewBillingSchedule: (body: BillingScheduleInput | { schedule: string }) =>
    request<BillingSchedulePreview>("/billing-schedules/preview", { method: "POST", body }),

  /** Every generation attempt for a schedule, newest first. */
  billingScheduleHistory: (id: string, signal?: AbortSignal) =>
    request<PaginatedBillingScheduleRuns>(
      `/billing-schedules/${encodeURIComponent(id)}/history`,
      { signal },
    ),

  // --- Dashboard and reports (FR-29) ----------------------------------

  billingDashboard: (params: BillingReportQuery = {}, signal?: AbortSignal) =>
    request<BillingDashboard>("/billing/dashboard", { query: params, signal }),

  billingAgeing: (signal?: AbortSignal) =>
    request<ReceivablesAgeing>("/billing/reports/ageing", { signal }),

  billingCustomerBalances: (signal?: AbortSignal) =>
    request<CustomerBalanceReport>("/billing/reports/customer-balances", { signal }),

  billingCollections: (params: BillingReportQuery = {}, signal?: AbortSignal) =>
    request<CollectionsReport>("/billing/reports/collections", { query: params, signal }),

  billingSales: (params: BillingReportQuery = {}, signal?: AbortSignal) =>
    request<SalesReport>("/billing/reports/sales", { query: params, signal }),

  billingCreditNotesReport: (params: BillingReportQuery = {}, signal?: AbortSignal) =>
    request<CreditNotesReport>("/billing/reports/credit-notes", { query: params, signal }),

  billingRecurringReport: (signal?: AbortSignal) =>
    request<RecurringReport>("/billing/reports/recurring", { signal }),

  /**
   * Download one CSV. Uses `sendBlob` so it shares the bearer header and the
   * token-refresh retry with every other call; the tenant and module gates on
   * the endpoint are exactly those of the matching list call.
   */
  billingExportCsv: (
    kind: BillingExportKind,
    params: BillingReportQuery = {},
    signal?: AbortSignal,
  ): Promise<Blob> =>
    sendBlob(`/billing/exports/${kind}.csv`, { query: params, signal }, false),

  /** The tax vocabulary, for the draft line editor. */
  taxes: (signal?: AbortSignal) => request<Tax[]>("/taxes", { signal }),

  createTax: (body: TaxWrite) =>
    request<Tax>("/taxes", { method: "POST", body }),

  updateTax: (id: string, body: Partial<TaxWrite>) =>
    request<Tax>(`/taxes/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  // --- Platform (superadmin) -------------------------------------------

  platformCompanies: (params: PlatformAccountQuery = {}, signal?: AbortSignal) =>
    request<PaginatedPlatformAccounts>("/platform/companies", { query: params, signal }),

  platformCompany: (id: string, signal?: AbortSignal) =>
    request<PlatformAccount>(`/platform/companies/${encodeURIComponent(id)}`, { signal }),

  provisionCompany: (body: PlatformAccountWrite) =>
    request<PlatformAccount & { temporary_password?: string }>("/platform/companies", {
      method: "POST",
      body,
    }),

  suspendCompany: (id: string, reason?: string, note?: string) =>
    request<PlatformAccount>(`/platform/companies/${encodeURIComponent(id)}/suspend`, {
      method: "POST",
      body: { reason: reason ?? "other", note: note ?? "" },
    }),

  activateCompany: (id: string, note?: string) =>
    request<PlatformAccount>(`/platform/companies/${encodeURIComponent(id)}/activate`, {
      method: "POST",
      body: { note: note ?? "" },
    }),

  platformSummary: (signal?: AbortSignal) =>
    request<PlatformSummary>("/platform/companies/metrics", { signal }),

  /** Seats vs caps for the whole customer list. */
  platformSummaryRows: (signal?: AbortSignal) =>
    request<CompanySummary[]>("/platform/companies/summary", { signal }),

  platformAudit: (signal?: AbortSignal) =>
    request<PaginatedOperatorAudit>("/platform/audit", { signal }),

  /**
   * Attempts to resume a session from the stored refresh token.
   *
   * Returns the user rather than a session envelope: refreshing mints a new
   * token pair but there is no fresh sign-in to report, and fabricating one
   * would mean inventing a shape the API never sent.
   */
  resumeSession: async (): Promise<CurrentUser | null> => {
    const token = await refreshAccessToken();
    if (!token) return null;
    try {
      return await api.me();
    } catch {
      clearTokens();
      return null;
    }
  },
};